import moment from 'moment'
import { useState } from 'react'

import {
  MANAGEMENT_QUERY_KEY_SEARCH_NON_ALLOCATED_SIMS,
  useManagementDeleteNonAllocatedSim,
  useManagementGetNonAllocatedSims
} from '@apps-common/apis/management/non-allocated-sim'
import { useManagementSearchSimApns } from '@apps-common/apis/management/sim-apn'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { Table } from '@apps-common/components/elements/table'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { valueStringCheck } from '@apps-common/helpers/value-string-check'
import { queryClient } from '@apps-common/lib'
import { ISimApnModel } from '@apps-common/types/management/sim-apn'

interface IProps {
  searchVal: string
}

export const NonAllocatedSimTableView: React.FC<IProps> = ({ searchVal }) => {
  const [skip, setSkip] = useState<number>(0)
  const [take, setTake] = useState<number>(10)

  const { isLoading: saIsLoading, isError: saIsError, data: simApns } = useManagementSearchSimApns({})
  const { isLoading, isError, data } = useManagementGetNonAllocatedSims({
    search: searchVal
  })

  const { mutate, isPending } = useManagementDeleteNonAllocatedSim(true, () => {
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_NON_ALLOCATED_SIMS() })
  })

  const keys = [
    {
      title: 'APN',
      key: 'simApnId',
      thClasses: '',
      tdClasses: '',
      Component: (props: any) => (
        <span>{valueStringCheck(props?.apns?.find((a: ISimApnModel) => a.simApnId === props.simApnId)?.simApnName)}</span>
      )
    },
    {
      title: 'Mobile#',
      key: 'mobileNo',
      thClasses: '',
      tdClasses: ''
    },
    {
      title: 'Sim#',
      key: 'simNo',
      thClasses: '',
      tdClasses: ''
    },
    {
      title: 'IP',
      key: 'ip',
      thClasses: '',
      tdClasses: ''
    },
    {
      title: 'Date loaded',
      key: 'dateLoaded',
      thClasses: '',
      tdClasses: '',
      Component: (props: any) => <span className='capitalize'>{moment(props.dateLoaded).format('DD/MM/YYYY')}</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      static: true,
      thClasses: 'text-right',
      tdClasses: '',
      Component: (props: any) => (
        <div className='flex items-center justify-end gap-[5px]'>
          <ModalButton
            classes='px-[5px]'
            component={ConfirmModal}
            disabled={isPending}
            componentProps={{
              text: 'Delete',
              description: 'Confirm you would like to delete this sim.',
              action: ConfirmModalAction.Error
            }}
            confirmCallback={() => {
              mutate({ id: props.id })
            }}
          >
            <ButtonRoundSm type='div' className='action-error'>
              <commonIcons.delete />
            </ButtonRoundSm>
          </ModalButton>
        </div>
      )
    }
  ]

  const loading = saIsLoading || saIsError || !simApns || isLoading || isError || !data

  return (
    <LayoutContainer>
      <LayoutPadding topPadding='pt-[65px] sm:pt-[25px]'>
        <Table
          loading={loading}
          loadingSkeletonHeight='leading-[30px]'
          placeholderSkeletonHeight='leading-[30px]'
          pagination={{
            loading,
            count: data?.length || 0,
            skip,
            take,
            onSkipChanged: (value: number) => setSkip(value),
            onTakeChanged: (value: number) => setTake(value)
          }}
          keys={keys}
          data={data?.map((s) => ({ ...s, apns: simApns })) || []}
          sliceData
          topLeftComponent={
            <>
              {!loading && data && (
                <div className='jost-light'>
                  <span className='jost-medium'>{data?.length}</span> sim results
                </div>
              )}
              {(loading || !data) && <div className='h-[22px] w-[100px] bg-skeleton rounded-lg' />}
            </>
          }
          onSkipChanged={(value: number) => setSkip(value)}
          onTakeChanged={(value: number) => setTake(value)}
        />
      </LayoutPadding>
    </LayoutContainer>
  )
}
